import {
  AfterViewInit,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  FormModelControl,
  SelectOption,
  TabsGroupComponent,
} from '@bcl/design-system';
import { Subject, takeUntil } from 'rxjs';
import {
  AddMonitorDialogData,
  AddMonitorDialogForm,
  AddMonitorDialogResponse,
  AddMonitorDialogProduct,
} from './add-monitor-dialog.model';
import { AddProductService } from '../../../area-monitor/components/consumables-dialog/consumables-dialog.service';
import { ScannerService } from '../../../../services/scanner.service';
import { ObjectPlacerCoordinatesSelectorData } from '../../../../components';

@Component({
  selector: 'app-add-monitor-dialog',
  templateUrl: './add-monitor-dialog.component.html',
  styleUrls: ['./add-monitor-dialog.component.scss'],
})
export class AddMonitorDialogComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  public readonly form: FormGroup<FormModelControl<AddMonitorDialogForm>>;

  public partOptions: SelectOption<number>[] = [];
  public instrumentOptions: SelectOption<number>[] = [];
  public productOptions: SelectOption[] = [];
  public warehouseOptions: SelectOption<number>[] = [];
  public selectedProduct: any = null;
  public maxAmount = 0;
  public productsList: any[] = [];
  public scannerActive = false;
  public showBaitNumber = false;
  public readonly mapTabIndex = 0;
  public readonly formTabIndex = 1;
  public coordinatesSelectorData:
    | ObjectPlacerCoordinatesSelectorData
    | undefined;

  @ViewChild('tabsGroup')
  private tabsGroup!: TabsGroupComponent;

  private scannerTargetControlName: 'serialNumber' | 'baitNumber' =
    'serialNumber';
  private allProducts: any[] = [];
  private selectedContractPartModuleId: string | null = null;
  private newMonitorCoordinates: { x: number; y: number } | undefined;

  private readonly destroyed$ = new Subject<void>();

  constructor(
    fb: FormBuilder,
    private dialogRef: MatDialogRef<
      AddMonitorDialogComponent,
      AddMonitorDialogResponse
    >,
    @Inject(MAT_DIALOG_DATA) public data: AddMonitorDialogData,
    private readonly addProductService: AddProductService,
    private readonly scannerService: ScannerService
  ) {
    dialogRef.updateSize('600px');
    dialogRef.disableClose = true;

    this.form = fb.group<FormModelControl<AddMonitorDialogForm>>({
      instrumentId: fb.control<number | null>(null, Validators.required),
      contractPartId: fb.control<number | null>(null, Validators.required),
      serialNumber: fb.control<string>(''),
      baitNumber: fb.control<string | null>(null),
      outside: fb.control<boolean>(false),
      temporary: fb.control<boolean>(false),
      bulk: fb.control<boolean>(false),
      insertCount: fb.control<number | null>(null),
      warehouseId: fb.control<number | null>({ value: null, disabled: true }),
      productId: fb.control<number | null>({ value: null, disabled: true }),
      amount: fb.control<number | null>({ value: null, disabled: true }),
      supplierId: fb.control<number | null>(null),
      monitorName: fb.control<string | null>(null, [Validators.maxLength(5)]),
      productsList: fb.control<AddMonitorDialogProduct[]>([]),
      allowToDisappear: fb.control<boolean>(false),
    }) as FormGroup<FormModelControl<AddMonitorDialogForm>>;

    this.partOptions = data.partOptions.map((x) => ({
      label: x.label,
      value: x.contractPartId,
    }));
    this.warehouseOptions = data.warehouses.map((x) => ({
      label: x.label,
      value: Number(x.value),
    }));

    this.form
      .get('warehouseId')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((warehouseId) => {
        this.form.get('productId')?.setValue(null);
        this.productOptions = [];
        const warehouseControl = this.form.get('warehouseId');
        if (warehouseId && warehouseControl?.enabled) {
          this.form.get('productId')?.enable();
          this.getProductsForWarehouse(warehouseId);
        } else {
          this.form.get('productId')?.disable();
          this.allProducts = [];
        }
      });

    this.form
      .get('bulk')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.updateMaxAmount());

    this.form
      .get('insertCount')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.updateMaxAmount());

    this.form
      .get('productId')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((rowId) => {
        this.selectedProduct = this.allProducts.find(
          (product) => product.rowId === rowId
        );
        this.updateMaxAmount();
        this.form.get('supplierId')?.setValue(this.selectedProduct?.supplierId);
        this.form.get('allowToDisappear')?.setValue(false);
        const amountControl = this.form.get('amount');
        if (rowId !== null && rowId !== undefined) {
          amountControl?.enable();
          amountControl?.setValidators([
            Validators.required,
            Validators.max(this.maxAmount),
          ]);
          amountControl?.setValue(1);
        } else {
          amountControl?.disable();
          amountControl?.clearValidators();
          amountControl?.setValue(null);
        }
        amountControl?.updateValueAndValidity();
      });

    this.form
      .get('instrumentId')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        const selected = this.data.instrumentOptions.find(
          (opt) => opt.value === value
        );
        this.showBaitNumber = selected?.token === 'BAIT';
        if (!this.showBaitNumber) {
          this.form.get('baitNumber')?.setValue(null);
        }
      });
  }

  public get canAllowToDisappear() {
    return this.selectedProduct?.allowToDisappear === true;
  }

  public ngOnInit(): void {
    this.form
      .get('contractPartId')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        this.form.get('instrumentId')?.reset(null);

        const part = this.data.partOptions.find(
          (y) => y.contractPartId === value
        );
        if (part) {
          this.selectedContractPartModuleId = part.moduleId;
          const filteredInstruments = this.data.instrumentOptions.filter(
            (x) => x.moduleId === part.moduleId
          );

          this.instrumentOptions = filteredInstruments.map((x) => ({
            label: x.label,
            value: x.value,
          }));

          this.form.get('warehouseId')?.enable();
          if (this.data.defaultCar) {
            this.form.get('warehouseId')?.setValue(this.data.defaultCar);
          }
        } else {
          this.selectedContractPartModuleId = null;
          this.instrumentOptions = [];
          this.form.get('warehouseId')?.disable();
          this.form.get('warehouseId')?.setValue(null);
          this.form.get('productId')?.disable();
          this.form.get('productId')?.setValue(null);
          this.productOptions = [];
          this.allProducts = [];
        }
      });
    this.form
      .get('bulk')
      ?.valueChanges.pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (value) {
          this.form.get('insertCount')?.setValidators([Validators.required]);
          this.form.get('serialNumber')?.setValue('');
          this.form.get('serialNumber')?.disable();
        } else {
          this.form.get('insertCount')?.clearValidators();
          this.form.get('serialNumber')?.enable();
        }
        this.form.get('insertCount')?.updateValueAndValidity();
      });
  }

  public ngAfterViewInit(): void {
    this.dialogRef.afterOpened().subscribe(() => {
      setTimeout(() => (this.coordinatesSelectorData = this.data.areaMapData));
    });
  }

  public onNewMonitorCoordinatesSelected(coordinates: {
    x: number;
    y: number;
  }): void {
    this.newMonitorCoordinates = coordinates;
    this.tabsGroup.selectTab(this.formTabIndex);
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public submit(): void {
    const formData = this.form.getRawValue();
    const result = {
      ...formData,
      areaId: this.data.areaId,
      warehouseId: formData.productId ? formData.warehouseId : null,
      statusId: '1',
      mapCoordinates: this.newMonitorCoordinates,
    } as AddMonitorDialogResponse;

    this.dialogRef.close(result);
  }

  public getProductsForWarehouse(warehouseId: number | null) {
    if (warehouseId) {
      this.addProductService
        .getProductsForWarehouse(warehouseId, this.selectedContractPartModuleId)
        .subscribe((products: any) => {
          this.allProducts = products?.elements ?? [];
          let counter = 0;
          this.allProducts.forEach((product) => {
            product.rowId = counter++;
          });
          this.productOptions = products?.elements.map((product: any) => ({
            value: product.rowId,
            label:
              product.name +
              (product.supplier ? ' | ' + product.supplier : '') +
              ' | ' +
              product.amount +
              ' ' +
              product.unit,
          }));
        });
    } else {
      this.productOptions = [];
    }
  }

  public addProductToList(): void {
    const rowId = this.form.get('productId')?.value;
    const amount = this.form.get('amount')?.value;
    const warehouseId = this.form.get('warehouseId')?.value;
    const supplierId = this.form.get('supplierId')?.value;
    const allowToDisappear = this.form.get('allowToDisappear')?.value || false;
    if (this.productsList.length === 0) {
      this.form.get('bulk')?.disable();
      this.form.get('insertCount')?.disable();
    }

    if (rowId === null || rowId === undefined || !amount || !warehouseId) {
      return;
    }

    const product = this.allProducts.find((p) => p.rowId === rowId);
    if (!product) {
      return;
    }

    const existingProduct = this.productsList.find(
      (p) =>
        p.productId === product.productId && p.supplierId === product.supplierId
    );
    if (existingProduct) {
      existingProduct.amount += amount;
    } else {
      this.productsList.push({
        productId: product.productId,
        name: product.name,
        amount,
        supplier: product.supplier,
        warehouseId,
        supplierId: product.supplierId,
        allowToDisappear,
      });
    }

    // Reset form controls
    this.form.get('productId')?.setValue(null);
    this.form.get('amount')?.setValue(null);
    this.form.get('amount')?.disable();
    this.form.get('productsList')?.setValue(this.productsList);
  }

  public removeProductFromList(productId: number, supplierId: number): void {
    this.productsList = this.productsList.filter(
      (p) => !(p.productId === productId && p.supplierId === supplierId)
    );
    this.form.get('productsList')?.setValue(this.productsList);

    // If the list is now empty, re-enable bulk and insertCount fields
    if (this.productsList.length === 0) {
      this.form.get('bulk')?.enable();
      this.form.get('insertCount')?.enable();
    }
  }

  public openScanner(
    target: 'serialNumber' | 'baitNumber' = 'serialNumber'
  ): void {
    this.scannerTargetControlName = target;
    this.scannerActive = true;
    const instanceId = 'add-monitor-dialog';

    this.scannerService
      .getScanResultObservable(instanceId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((result: string) => {
        this.form.get(this.scannerTargetControlName)?.setValue(result);
        this.scannerActive = false;
      });
  }

  public closeScanner(): void {
    this.scannerActive = false;
  }

  private updateMaxAmount(): void {
    if (!this.selectedProduct) {
      this.maxAmount = 0;
      return;
    }

    const isBulk = this.form.get('bulk')?.value;
    const insertCount = this.form.get('insertCount')?.value;
    const rowId = this.form.get('productId')?.value;

    // Calculate the total amount already in the productsList for this product
    const existingAmount = this.productsList
      .filter(
        (p) =>
          p.productId === this.selectedProduct.productId &&
          p.supplierId === this.selectedProduct.supplierId
      )
      .reduce((sum, p) => sum + p.amount, 0);

    // Calculate the maximum available amount based on bulk/insert count
    let maxAvailableAmount;
    if (isBulk && insertCount) {
      maxAvailableAmount = Math.floor(
        this.selectedProduct.amount / insertCount
      );
    } else {
      maxAvailableAmount = this.selectedProduct.amount;
    }

    // Subtract the existing amount from the maximum available
    this.maxAmount = Math.max(0, maxAvailableAmount - existingAmount);

    const amountControl = this.form.get('amount');
    if (amountControl?.enabled) {
      amountControl.setValidators([
        Validators.required,
        Validators.max(this.maxAmount),
      ]);
      amountControl.updateValueAndValidity();
    }
  }
}
